import {
  SubmissionDocumentInput,
  SubmissionDocumentType,
  GetSubmissionQuery,
  SignatureStatus,
} from "generated-graphql/graphql";
import { type SubmissionFormData, type SubmissionReviewerSigner } from "hooks/useSubmissionForm";

export type CalculateNeedsDocumentPreparationOptions = {
  isDigitalSignature: boolean;
  isEditMode: boolean;
  defaultValues: SubmissionFormData;
  signer: SubmissionReviewerSigner | null;
  submissionData?: GetSubmissionQuery;
  submissionDocuments: SubmissionDocumentInput[];
};

export function computeNeedsDocumentPreparation({
  isDigitalSignature,
  isEditMode,
  defaultValues,
  signer,
  submissionData,
  submissionDocuments,
}: CalculateNeedsDocumentPreparationOptions): boolean {
  if (!isDigitalSignature) {
    return false;
  }

  if (!isEditMode) {
    // new digital signing submissions always need document preparation
    return true;
  }

  const initialSigner = defaultValues.reviewers.find((r) => r.isSigner);

  // Fall back to the server-side signer when the signer state hasn't been
  // hydrated yet (e.g. initial mount before the signer init effect fires).
  const signerIdForComparison =
    signer?.id ?? submissionData?.submission?.reviewers?.find((r) => r.isSigner)?.user.id;

  if (
    initialSigner?.userId !== signerIdForComparison ||
    (initialSigner && calculateReviewerNeedsDocumentPreparation(initialSigner, submissionData))
  ) {
    return true;
  }

  const NON_ATTACHMENT_TYPES = [
    SubmissionDocumentType.Submission,
    SubmissionDocumentType.RejectedSubmission,
    SubmissionDocumentType.SignedSubmission,
  ];

  const serverSubmissionDocuments = (submissionData?.submission?.documents ?? []).filter((sd) =>
    NON_ATTACHMENT_TYPES.includes(sd.type)
  );

  const initialServerSubmissionDocumentIds = serverSubmissionDocuments.map((sd) => sd.document.id);

  // Extract document IDs from the submission document inputs
  const submissionDocumentIds = submissionDocuments.map((d) => d.documentId);

  const initialSet = new Set(initialServerSubmissionDocumentIds);
  const hasPendingClientSubmissionDocument = submissionDocumentIds.some(
    (documentId) => !initialSet.has(documentId)
  );

  // True if the submission documented that's persisted to the submission
  // is removed and a new submission document is added and has not been
  // saved yet. ( this new document needs to be prepped!)
  if (hasPendingClientSubmissionDocument) {
    return true;
  }

  if (submissionDocumentIds.length === 0) {
    return true;
  }

  const signerReviewer = submissionData?.submission?.reviewers?.find((r) => r.isSigner);
  if (
    signerReviewer &&
    signerReviewer.signatureStatus !== SignatureStatus.Sent &&
    signerReviewer.signatureStatus !== SignatureStatus.Completed
  ) {
    return true;
  }

  return false;
}

function calculateReviewerNeedsDocumentPreparation(
  reviewer: Pick<SubmissionFormData["reviewers"][number], "userId" | "isSigner">,
  submissionData: GetSubmissionQuery | undefined
) {
  if (!reviewer.isSigner || !submissionData) {
    return false;
  }

  const matchingReviewer = submissionData.submission?.reviewers.find(
    (r) => r.user.id === reviewer.userId
  );
  if (!matchingReviewer) {
    return false;
  }

  return (
    matchingReviewer.signatureStatus !== SignatureStatus.Completed &&
    matchingReviewer.signatureStatus !== SignatureStatus.Sent
  );
}
